import {
  getFilterSubTypeTitle,
  getFilterTitle,
} from "helpers/utility/queryFilterHelpers";
import { FilterMenuItemType, FilterSubType, Position, Filters } from "types";
import { MIN_SPACE_RIGHT_OF_ROW_PX } from "views/components/QueryPanel/AddFilter";
import { getTextWidth } from "helpers/utility/utilities";
import { useEffect, useRef, useState } from "react";
import XIcon from "assets/icons/XIcon";
import classNames from "classnames";
import QueryTabImage from "views/components/QueryImage/QueryImage.tsx";
import { useSelector } from "react-redux";
import { getSelectedQueryModal } from "redux/SingleDataset/selectors";

import styles from "./style.module.scss";

export const TAB_HEIGHT_WITH_OFFSET = 34;

export interface QueryTabProps {
  labels: string[];
  operator: string;
  isRange?: boolean;
  noColon?: boolean;
  icon?: JSX.Element;
  type: FilterMenuItemType;
  subType?: FilterSubType | null;
  onRemove: () => void;
  onClick?: (p: Position) => void;
  id: string;
  image?: {
    uri: string;
    boundingBox?: [number, number, number, number];
  };
  interactive?: boolean;
}

export default function QueryTab({
  icon,
  type,
  labels,
  operator,
  isRange = false,
  noColon = false,
  subType = null,
  onClick,
  onRemove,
  id,
  image,
  interactive = true,
}: QueryTabProps) {
  const MAX_LABELS = 3;
  const isOverflowing = labels.length > MAX_LABELS;
  const labelsRef = useRef<any>(null);
  const [modalPosition, setModalPosition] = useState<Position | null>(null);

  const joiningOperator = isRange ? "-" : ",";
  const labelsText = labels.join(joiningOperator);

  const queryTabRef = useRef<HTMLDivElement>(null);
  const selectedQueryModal = useSelector(getSelectedQueryModal);

  // Check if this is a visual similarity query that's still loading image data
  const isVisualSimilarityLoading =
    type === Filters.VISUAL_SIMILARITY && !image;

  // Check if this tab's modal is currently open
  const isModalOpen =
    selectedQueryModal &&
    selectedQueryModal.id === id &&
    selectedQueryModal.type === type;

  const queryTabClassnames = classNames(styles.queryTab, {
    [styles.noPointer]: !onClick,
    [styles.withImage]: image?.uri,
    [styles.loading]: isVisualSimilarityLoading,
    [styles.visualSimilaritySelected]:
      isModalOpen && type === Filters.VISUAL_SIMILARITY,
    [styles.interactive]: interactive,
  });
  const labelsClassnames = classNames(styles.labels, {
    [styles.overflow]: isOverflowing,
  });

  const truncateLabel = (label: string | number) => {
    if (typeof label === "number") return label;

    // check for character limit of 21
    if (label.length > 21) {
      return `...${label.slice(-18)}`;
    }

    const lastDotIndex = label.lastIndexOf(".");
    if (lastDotIndex > 0) {
      const namePart = label.slice(0, lastDotIndex);
      const extension = label.slice(lastDotIndex);
      return namePart.length > 8
        ? `...${namePart.slice(-8)}${extension}`
        : label;
    }
    return label;
  };

  const getOperatorText = () => {
    let subText = "";

    if (subType) {
      subText = ` ${getFilterSubTypeTitle(subType)}:`;
    } else if (!noColon) {
      subText = ":";
    }

    return operator + subText;
  };

  const displayLabels = labels.map(truncateLabel).join(joiningOperator);

  const handleClick = () => {
    if (modalPosition && onClick) {
      onClick(modalPosition);
    }
  };

  useEffect(() => {
    if (isOverflowing && labelsRef.current) {
      let totalWidth = labels.slice(0, 3).reduce((width, label) => {
        const trucatedLabel = truncateLabel(label) as string;
        return width + getTextWidth(trucatedLabel, "14px");
      }, 0);

      labelsRef.current.style.setProperty(
        "--maxuimum-width",
        `${totalWidth + 25}px`,
      );
    }
  }, [labels, isOverflowing]);

  useEffect(() => {
    const tab = queryTabRef.current;

    if (tab) {
      const rect = tab.getBoundingClientRect();
      const spaceOnRightOfAnchor = window.innerWidth - rect.left;
      const isRightAligned = spaceOnRightOfAnchor >= MIN_SPACE_RIGHT_OF_ROW_PX;

      const left = isRightAligned ? rect.left : rect.right;

      setModalPosition({
        top: rect.top + TAB_HEIGHT_WITH_OFFSET,
        left: left,
        align: isRightAligned ? "start" : "end",
      });
    }
  }, [labels]);

  return (
    <div
      className={queryTabClassnames}
      data-testid="query-tab"
      data-query-id={id}
      ref={queryTabRef}
      onClick={handleClick}
    >
      {icon && <div className={classNames(styles.icon)}>{icon}</div>}

      {image && (
        <div className={classNames(styles.imageWrapper, "queryImageWrapper")}>
          <QueryTabImage image={image} />
        </div>
      )}
      {isVisualSimilarityLoading && (
        <div className={classNames(styles.loadingSkeleton)} />
      )}
      {!image && !isVisualSimilarityLoading && (
        <div className={styles.textContainer}>
          <div className={styles.textOusideLabels}>
            <span className={styles.text}>{getFilterTitle(type)}</span>
            <span
              className={styles.operator}
              data-testid="query-tab-operator-text"
            >
              {getOperatorText()}
            </span>
          </div>
          <div
            data-testid="query-tab-labels-text"
            className={labelsClassnames}
            title={labelsText}
            ref={labelsRef}
          >
            {displayLabels}
          </div>
        </div>
      )}
      {interactive && (
        <div
          data-testid="query-tab-close-btn"
          className={classNames(styles.closeBtn)}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <XIcon />
        </div>
      )}
    </div>
  );
}
